import React, { useEffect, useState } from "react"
import { DropdownType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import * as ToggleGroup from "@radix-ui/react-toggle-group"
import { useMutation, useQueries } from "@tanstack/react-query"
import { Key } from "lucide-react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"

import { TypeSubmitGroup, TypeSubmitIndividual } from "@/types/announcement"
import { ConnectionType } from "@/types/zone-connection-rates-type"
import {
  getBooksZone,
  getConnectionStatus,
  getZoneDropdown,
} from "@/lib/api/billing"
import { getEmailListDropdown, sendAnnouncment } from "@/lib/api/consumers"
import { getConnectionType } from "@/lib/api/meter"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { MultiSelect } from "@/components/multie-select"

import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Textarea } from "../ui/textarea"

type BooksDropdownType = {
  id: number
  name: string
  zone_id: number
  zone: string
  description: string
}

const GroupformSchema = z.object({
  zone_id_list: z.array(z.number()).min(1, "Please enter a zone"),
  book_id_list: z.array(z.number()).min(1, "Please enter a book"),
  connection_status_id_list: z.array(z.number()).optional(),
  connection_type_id_list: z.array(z.number()).optional(),
  subject: z.string().min(1, "Please enter a subject"),
  header: z.string().min(1, "Please enter a header"),
  body: z.string().min(1, "Enter your message please"),
})

const IndividualFormSchema = z.object({
  consumer_id_list: z.array(z.number()).min(1, "Please enter a recipient"),
  subject: z.string().min(1, "Please enter a subject"),
  header: z.string().min(1, "Please enter a header"),
  body: z.string().min(1, "Enter a message"),
})

export function AnnouncementForm() {
  const [clearForm, setClearForm] = useState<boolean>(false)
  const [zones, setZones] = useState<number[]>([])
  const [books, setBooks] = useState<number[]>([])
  const [mode, setMode] = useState("individual")
  const [showSelectedZone, setSelectedZones] = useState<number[]>([])
  const [showSelectedBooksByZone, setSelectedBooksByzone] = useState<
    Array<{
      id: number
      label: string
      value: string
      zone_id: number
    }>
  >([])

  const queries = useQueries({
    queries: [
      {
        queryKey: ["email-list-dropdown"],
        queryFn: getEmailListDropdown,
      },
      {
        queryKey: ["zone-book-dropdown"],
        queryFn: getZoneDropdown,
      },
      {
        queryKey: ["connection-type"],
        queryFn: getConnectionType,
      },
      {
        queryKey: ["connection-status"],
        queryFn: getConnectionStatus,
      },
      ...showSelectedZone.map((zoneId) => ({
        queryKey: ["bookzone", zoneId],
        queryFn: async () => {
          const books = await getBooksZone(zoneId)
          setSelectedBooksByzone(books) // ✅ Appending correctly
          return books
        },
        enabled: !!zoneId, // ✅ Ensures it only runs when zoneId exists
      })),
    ],
  })

  type DropdownEmailList = {
    email: string
    consumer_id: number
  }

  const emailDropDown: Array<{
    label: string
    value: number
    id?: number
  }> =
    queries[0]?.data?.map((item: DropdownEmailList) => ({
      label: item.email,
      value: item.consumer_id,
      id: item.consumer_id, // Use consumer_id instead of id (if applicable)
    })) || []

  const queryConfigs = [
    {
      queryKey: ["zones"],
      queryFn: getZoneDropdown,
    },
    ...(zones && zones.length > 0
      ? zones.map((zoneId) => ({
          queryKey: ["booksZone", zoneId],
          queryFn: () => getBooksZone(zoneId),
        }))
      : []),
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  // Zones and books data
  const zonesData = results[0]?.data || []
  const booksData = results.slice(1).map((res) => res.data || [])

  // Group books by zone_id
  const booksByZone = booksData.reduce(
    (acc, books) => {
      books.forEach((book: BooksDropdownType) => {
        if (!acc[book.zone_id]) acc[book.zone_id] = []
        acc[book.zone_id].push(book)
      })
      return acc
    },
    {} as Record<number, BooksDropdownType[]>,
  )
  const booksPerZoneDropdown = zonesData
    .map((zone: DropdownType) => {
      if (
        zone.id !== undefined &&
        zones.includes(zone.id) &&
        booksByZone[zone.id]?.length > 0
      ) {
        return booksByZone[zone.id].map((book: DropdownType) => ({
          id: book.id,
          label: `${zone.label} - ${book.label}`,
          value: book.label,
        }))
      }
      return null
    })
    .filter((books: BooksDropdownType) => books !== null)
    .flat()

  type DropdownZoneBook = {
    id: number
    label: string
    value: string
  }

  const zoneBookDropdown: Array<{ label: string; value: number; id?: number }> =
    queries[1]?.data?.map((item: DropdownZoneBook) => ({
      label: item.label,
      value: item.id,
      id: item.id,
    })) || []

  const connectionTypeDropdown: Array<{
    label: string
    value: number
    id?: number
  }> =
    queries[2]?.data?.map((item: ConnectionType) => ({
      label: item.type_name,
      value: item.type_name,
      id: item.id,
    })) || []

  const connectionStatusDropdown: Array<{
    label: string
    value: number
    id?: number
  }> = queries[3]?.data?.map(
    (item: { label: string; id: number; value: number }) => ({
      label: item.label,
      Value: item.label,
      id: item.id,
    }),
  )

  const bookByZoneDropdown: Array<{
    label: string
    value: number
    id?: number
  }> = showSelectedBooksByZone.map((item) => ({
    label: item.label,
    value: item.id,
    id: item.id,
  }))

  const form = useForm({
    resolver: zodResolver(
      mode == "group" ? GroupformSchema : IndividualFormSchema,
    ),
    defaultValues:
      mode == "group"
        ? {
            recipient: "",
            zone_id_list: [],
            book_id_list: [],
            connection_status_id_list: [],
            connection_type_id_list: [],
            subject: "",
            header: "",
            body: "",
          }
        : {
            consumer_id_list: [],
            subject: "",
            header: "",
            body: "",
          },
  })
  const { toast } = useToast()
  const mutation = useMutation({
    mutationFn: async (payload: TypeSubmitGroup | TypeSubmitIndividual) => {
      sendAnnouncment(payload)
    },
    onSuccess: () => {
      toast({
        description: "Announcement send successfully.",
      })
      setClearForm(!clearForm)
      form.reset({
        consumer_id_list: [],
        header: "",
        subject: "",
        body: "",
      })
    },
  })

  function onSubmitForm(values: any) {
    let payload: TypeSubmitGroup | TypeSubmitIndividual

    if (mode === "group") {
      payload = {
        zone_id_list: values.zone_id_list,
        book_id_list: values.book_id_list,
        connection_status_id_list: values.connection_status_id_list,
        connection_type_id_list: values.connection_type_id_list,
        header: values.header,
        subject: values.subject,
        body: values.body,
      } as TypeSubmitGroup
    } else {
      payload = {
        consumer_id_list: values.consumer_id_list,
        header: values.header,
        subject: values.subject,
        body: values.body,
      } as TypeSubmitIndividual
    }
    mutation.mutate(payload)
  }

  return (
    <Card className="">
      <CardHeader className="flex flex-row items-center gap-4 text-3xl font-normal">
        <h1>Create New Announcement</h1>
        <ToggleGroup.Root
          type="single"
          value={mode}
          onValueChange={(value) => value && setMode(value)}
          className="flex gap-2 rounded-lg border bg-muted p-1"
        >
          <ToggleGroup.Item
            value="individual"
            className={cn(
              "rounded-md px-4 py-2 text-sm font-medium transition",
              mode === "individual"
                ? "bg-primary text-white"
                : "bg-transparent",
            )}
          >
            Individual
          </ToggleGroup.Item>
          <ToggleGroup.Item
            value="group"
            className={cn(
              "rounded-md px-4 py-2 text-sm font-medium transition",
              mode === "group" ? "bg-primary text-white" : "bg-transparent",
            )}
          >
            Group
          </ToggleGroup.Item>
        </ToggleGroup.Root>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmitForm)}>
            {mode === "group" ? (
              <>
                <div className="my-2 flex items-center gap-2">
                  <FormField
                    control={form.control}
                    name="zone_id_list"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Zone</FormLabel>
                        <MultiSelect
                          clearForm={clearForm}
                          setClearForm={setClearForm}
                          value={field.value || []}
                          options={zoneBookDropdown}
                          onValueChange={(selectedValues) => {
                            field.onChange(selectedValues)
                            setZones(selectedValues)
                          }}
                          placeholder="Select option"
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="book_id_list"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Book</FormLabel>
                        <MultiSelect
                          clearForm={clearForm}
                          setClearForm={setClearForm}
                          options={booksPerZoneDropdown}
                          onValueChange={(selectedValues) => {
                            field.onChange(selectedValues)
                          }}
                          placeholder="Select option"
                          {...field}
                          idAsValue
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="my-2 flex items-center gap-2">
                  <FormField
                    control={form.control}
                    name="connection_status_id_list"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel className="text-[12px]">
                          Filter by Connection Status
                        </FormLabel>
                        {
                          <MultiSelect
                            setClearForm={setClearForm}
                            clearForm={clearForm}
                            value={field.value || []}
                            options={connectionStatusDropdown}
                            onValueChange={(selectedValues) => {
                              field.onChange(selectedValues)
                            }}
                            placeholder="Select option"
                            idAsValue
                          />
                        }
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="connection_type_id_list"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel className="text-[12px]">
                          Filter by Connection Type
                        </FormLabel>
                        <MultiSelect
                          clearForm={clearForm}
                          setClearForm={setClearForm}
                          value={field.value || []}
                          options={connectionTypeDropdown}
                          onValueChange={(selectedValues) => {
                            field.onChange(selectedValues)
                            console.log(selectedValues)
                          }}
                          placeholder="Select option"
                          idAsValue
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem className="flex flex-1 flex-col">
                        <FormLabel>Subject</FormLabel>
                        <Input
                          type="text"
                          id="subject"
                          placeholder="Field text goes here"
                          {...field}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="header"
                  render={({ field }) => (
                    <FormItem className="mb-4 flex flex-1 flex-col">
                      <FormLabel>Header</FormLabel>
                      <Input
                        type="text"
                        id="subject"
                        placeholder="Field text goes here"
                        {...field}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <>
                      <Textarea
                        placeholder="Message text goes here"
                        className="h-52"
                        {...field}
                      />
                      <FormMessage />
                    </>
                  )}
                />
              </>
            ) : (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="consumer_id_list"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Recipient</FormLabel>
                      <MultiSelect
                        clearForm={clearForm}
                        setClearForm={setClearForm}
                        value={field.value || []} // Ensure value is always defined
                        onValueChange={(selectedValue) => {
                          field.onChange(selectedValue)
                        }}
                        options={emailDropDown}
                        placeholder="Select option"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Subject</FormLabel>
                      <Input
                        type="text"
                        placeholder="Field text goes here"
                        {...field}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="header"
                  render={({ field }) => (
                    <FormItem className="flex flex-1 flex-col">
                      <FormLabel>Header</FormLabel>
                      <Input
                        type="text"
                        placeholder="Field text goes here"
                        {...field}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <>
                      <Textarea
                        placeholder="Message text goes here"
                        className="h-52"
                        {...field}
                      />
                      <FormMessage />
                    </>
                  )}
                />
              </div>
            )}
            <div className="my-4 flex items-center gap-2">
              <div className="relative bg-red-50">
                <Button type="submit" className="px-16">
                  Send Now
                </Button>
              </div>
              <Icons.font height="15px" width="15px" />
              <Icons.attachment height="15px" width="15px" />
              <Icons.insertLink height="15px" width="15px" />
              <Icons.emoji height="15px" width="15px" />
              <Icons.attachImage height="15px" width="15px" />
              <Icons.sign height="15px" width="15px" />
              <Icons.label height="15px" width="15px" />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
